import { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Grid, Button, SvgIcon, Stack, useMediaQuery, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Link, useTheme, Skeleton, Snackbar, Alert } from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { SearchLg, SearchSm, Download01 } from '../../../assets';
import { CatalogueProductsFilterOptions, Category, client, Item } from '../../../app/services/api/orderManagementClient';
import useLogError from '../../../hooks/useLogError';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import { Section } from '../../../components/ui/Section';
import CustomBreadCrumbs from '../../../components/ui/CustomBreadCrumbs';
import CatalogueProductsTable from './components/CatalogueProductsTable';
import { UserContext } from '../../../components/shared/useUser';

const CatalogueDetails = () => {
  const { t } = useTranslation('catalogues');
  const { logError } = useLogError();
  const [isLoading, setIsLoading] = useState(true);
  const { id } = useParams();
  const [catalogueName, setCatalogueName] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Item[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc'); const [orderBy, setOrderBy] = useState('');
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`); const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      const categoriesPromise = fetchCategories(id);
      const productsPromise = fetchCatalogueProducts(id, getFilterOptions(false));
      Promise.all([categoriesPromise, productsPromise])
        .finally(() => setIsLoading(false));
    }
  }, [id, page, order, orderBy]);

  const fetchCategories = (catalogueId: string) => {
    return client.getCategoriesByCatalogue(selectedStore?.storeNumber ?? '', catalogueId)
      .then(response => setCategories(response.data))
      .catch(logError);
  };

  const fetchCatalogueProducts = (catalogueId: string, filters: CatalogueProductsFilterOptions) => {
    return client.getCatalogueProducts(catalogueId, filters)
      .then((response) => {
        setProducts([...response.data.items.items]);
        setTotalPages(response.data.items.totalPages);
        setCatalogueName(response.data.catalogueName);
      })
      .catch(logError);
  };
  const getFilterOptions = (resetPage: boolean) => {
    const filterOptions: CatalogueProductsFilterOptions = {
      storeNumber: selectedStore?.storeNumber,
      searchTerm: searchTerm,
      category: categoryFilter.join(','),
      pageNumber: resetPage ? 0 : page,
      pageSize: 10,
      sort: orderBy,
      direction: order,
    };
    return filterOptions;
  };

  const handleCategorySelect = (event: SelectChangeEvent<string[]>) => {
    const { target: { value } } = event;
    const selectedStatuses = typeof value === 'string' ? value.split(',') : value;
    setCategoryFilter(selectedStatuses);
  };
  const clearAll = () => {
    fetchCatalogueProducts(id!, {
      storeNumber: selectedStore?.storeNumber,
      searchTerm: '',
      category: '',
      pageNumber: 0,
      pageSize: 10,
      sort: orderBy,
      direction: order,
    });
    setSearchTerm('');
    setCategoryFilter([]);
    setPage(0);
  };

  const handleSearch = () => {
    fetchCatalogueProducts(id!, getFilterOptions(true));
    setPage(0);
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDownloadExportAttributes = async () => {

    if (!id) return;

    setIsDownloading(true);
    try {
      const response = await client.downloadCatalogueExportAttributes(selectedStore?.storeNumber ?? '', id);
      if (response.success && response.data) {
        const byteCharacters = atob(response.data.fileData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.fileName || `catalogue-${id}-export-attributes.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        handleSnackbarOpen(t('filterPanel.downloadSuccess'), 'success');
      } else {
        handleSnackbarOpen(t('filterPanel.downloadError'), 'error');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('filterPanel.downloadError'), 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleUploadExportAttributes = async (file: File) => {
    if (!id) return;

    setIsUploading(true);
    try {
      const fileData = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const response = await client.uploadCatalogueExportAttributes(selectedStore?.storeNumber ?? '', id, {
        fileData: fileData,
        fileName: file.name,
      });
      if (response.success) {
        fetchCatalogueProducts(id, getFilterOptions(false));
        handleSnackbarOpen(t('filterPanel.uploadSuccess'), 'success');
      } else {
        handleSnackbarOpen(t('filterPanel.uploadError'), 'error');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('filterPanel.uploadError'), 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUploadExportAttributes(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <PageArea>
      <Box>
        <CustomBreadCrumbs title={`${t('details.pageName')} `}>
          <Link to="/catalogue-list"
            component={RouterLink}>{t('title')}</Link>
        </CustomBreadCrumbs>
      </Box>
      <Box
        display='flex'
        width='100%'
        height='100%'
        flexDirection='column'
      >
        <Grid container>
          <Stack
            width='100%'
            spacing={2}
            direction={isMobile ? 'column' : 'row'}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Box width={isMobile ? '100%' : 'auto'}>
              <PageTitle>{t('details.title')} - {catalogueName}</PageTitle>
            </Box>
          </Stack>
          <Grid container
            sx={{ pt: '6px', pb: '12px' }}
            spacing={4}>
            <Grid item
              lg={4}
              sm={6}
              xs={12}>
              <TextField
                fullWidth
                size='small'
                placeholder={t('filterPanel.search')}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchLg />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item
              lg={4}
              sm={6}
              xs={12}>

              <FormControl
                variant="outlined"
                size='small'
                fullWidth
              >
                <InputLabel>
                  {t('filterPanel.category')}
                </InputLabel>
                <Select
                  multiple
                  value={categoryFilter}
                  label={t('filterPanel.category')}
                  onChange={handleCategorySelect}
                >
                  {categories.map(x =>
                    <MenuItem key={x.id}
                      value={x.id}>{x.name}</MenuItem>,
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item
              lg={1}
              sm={3}
              xs={6}>
              <Button
                variant="secondary"
                size="lg"
                onClick={clearAll}
                fullWidth
              >
                {t('filterPanel.clear')}
              </Button>
            </Grid>
            <Grid item
              lg={1}
              sm={3}
              xs={6}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right' }}>
              <Button
                variant="primary"
                size="lg"
                startIcon={<SvgIcon> <SearchSm /> </SvgIcon>}
                onClick={handleSearch}
                fullWidth>
                {t('filterPanel.search')}
              </Button>
            </Grid>
            { hasPermissionTo(['CataloguesExport']) && 
              <Grid item
                lg={1}
                sm={3}
                xs={6}>
                <Button
                  variant="primary"
                  size="lg"
                  startIcon={<SvgIcon><Download01 /></SvgIcon>}
                  onClick={handleDownloadExportAttributes}
                  disabled={isDownloading}
                  fullWidth
                >
                  {isDownloading ? t('filterPanel.downloading') : t('filterPanel.download')}
                </Button>
              </Grid> 
            }
            { hasPermissionTo(['CataloguesImport']) && 
              <Grid item
                lg={1}
                sm={3}
                xs={6}>
                <Button
                  variant="primary"
                  size="lg"
                  startIcon={<UploadFile />}
                  onClick={handleFileUpload}
                  disabled={isUploading}
                  fullWidth
                >
                  {isUploading ? t('filterPanel.uploading') : t('filterPanel.upload')}
                </Button>
                <input aria-hidden
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".csv,.xlsx,.xls"
                  style={{ display: 'none' }}
                />
              </Grid>
            }
          </Grid>
        </Grid>
        <Section>
          {isLoading
            ?
            <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
              variant='rectangular'
              height={445.89}
            />
            :
            <CatalogueProductsTable
              catalogueId={id}
              catalogueName={catalogueName}
              list={products}
              isLoading={isLoading}
              searchTerm={searchTerm}
              clearAll={clearAll}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              canViewDetails={hasPermissionTo(['ProductDetailView'])} 
            />}
        </Section>
      </Box>
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default CatalogueDetails;